import React from "react";
import { Button, Column, filterMap, Heading, Modal, Row, Tooltip, TruncatedText } from "@hex-insights/core";
import { InternalLink } from "@hex-insights/router";
import { Badge } from "../../../../../Components/Badge";
import { DetailFormModalController } from "../../../../../Components/DetailFormModal";
import { Icon } from "../../../../../Components/Icon";
import { CourseSectionDetailQuery, CourseSectionPeriodFilterInput } from "../../../../../Utilities";
import { AcademicTermFormat, CourseSectionFormat, HomeRoomSectionFormat } from "../../../../Format";
import { CourseSectionForm } from "../../../../Forms";
import { AcademicTermLink, HomeRoomSectionLink } from "../../../../Links";
import {
	AcademicTermUtilities,
	CourseSectionUtilities,
	StudentCourseSectionEnrollmentUtilities,
	TeacherCourseSectionEnrollmentUtilities,
} from "../../../../Utilities";
import * as ClassYearComponents from "../../../ClassYear";
import * as CourseSectionPeriodComponents from "../../../CourseSectionPeriod";
import * as EmployeeComponents from "../../../Employee";
import * as StudentComponents from "../../../Student";
import styles from "./styles.module.css";

export type HeaderProps = {
	courseSection: CourseSectionDetailQuery["courseSection"];
	courseSectionPeriodTo?: string;
};

export function Header({ courseSection, courseSectionPeriodTo }: HeaderProps) {
	const color = CourseSectionUtilities.getColor(courseSection);

	return (
		<Row
			justify="space-between"
			align="center"
			className={styles["header"]}
			style={{ "--course-section-color": color } as React.CSSProperties}
		>
			<Column justify="spaced-start" style={{ width: "100%" }}>
				<Row justify="spaced-start" align="center">
					<Heading.H1 noMargin>
						<TruncatedText lines={2}>{CourseSectionFormat.name(courseSection)}</TruncatedText>
					</Heading.H1>

					<Tooltip.Container style={{ flexShrink: 0 }}>
						<Tooltip>
							<Tooltip.Body>
								<span className="nowrap">
									{AcademicTermFormat.Fields.startDate(courseSection.academicTerm.startDate)} to{" "}
									{AcademicTermFormat.Fields.endDate(courseSection.academicTerm.endDate)}
								</span>
							</Tooltip.Body>
						</Tooltip>

						<AcademicTermLink instance={courseSection.academicTerm} className="link--no-text-decoration">
							<Badge className={styles["header__badge"]}>
								<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
									{AcademicTermFormat.name(courseSection.academicTerm)}
								</Row>
							</Badge>
						</AcademicTermLink>
					</Tooltip.Container>

					<ClassYearComponents.ClassYearsBadge
						classYears={courseSection.course.classYears}
						className={styles["header__badge"]}
					/>

					{!!courseSection.homeRoomSection && (
						<HomeRoomSectionLink instance={courseSection.homeRoomSection} className="link--no-text-decoration">
							<Badge className={styles["header__badge"]}>
								<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
									{HomeRoomSectionFormat.name(courseSection.homeRoomSection)}
								</Row>
							</Badge>
						</HomeRoomSectionLink>
					)}

					<ScheduleController courseSection={courseSection} />
				</Row>

				<Row justify="space-between" align="flex-end">
					<Row justify="spaced-start" horizontalSpacing="1.5rem">
						<TeachersList courseSection={courseSection} />
						<StudentsList courseSection={courseSection} />
					</Row>

					{!!courseSectionPeriodTo && (
						<InternalLink
							to={courseSectionPeriodTo}
							className="link--no-text-decoration"
							style={{ color: "#fff", fontWeight: "bold" }}
						>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
								Go to Period <Icon.ArrowRight size={16} />
							</Row>
						</InternalLink>
					)}
				</Row>
			</Column>

			{/* TODO image/icon */}

			<div className={styles["header__edit-button__container"]}>
				<DetailFormModalController
					heading="Edit Course Section"
					DetailForm={CourseSectionForm.ControlledDetail}
					instanceName="courseSection"
					instance={courseSection}
					className={styles["header__edit-button"]}
				/>
			</div>
		</Row>
	);
}

type ScheduleControllerProps = {
	courseSection: HeaderProps["courseSection"];
};

function ScheduleController({ courseSection }: ScheduleControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const initialDate = AcademicTermUtilities.isActive(courseSection.academicTerm)
		? undefined
		: new Date(courseSection.academicTerm.startDate);

	const filters = React.useMemo<CourseSectionPeriodFilterInput>(
		() => ({
			courseSectionIDEQ: courseSection.id,
		}),
		[courseSection.id],
	);

	return (
		<React.Fragment>
			<Button onClick={openModal} style={{ padding: "0", border: "none", borderRadius: 0, cursor: "pointer" }}>
				<Badge className={styles["header__badge"]}>
					<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
						<Icon.CalendarBlank size={16} /> View Schedule
					</Row>
				</Badge>
			</Button>

			<Modal.If condition={isModalOpen}>
				<Modal size="large" onClose={closeModal}>
					<Modal.Body style={{ height: "100%" }}>
						<CourseSectionPeriodComponents.ScheduleCalendar
							initialDate={initialDate}
							filters={filters}
							style={{ height: "100%" }}
						/>
					</Modal.Body>
				</Modal>
			</Modal.If>
		</React.Fragment>
	);
}

type TeachersListProps = {
	courseSection: HeaderProps["courseSection"];
};

function TeachersList({ courseSection }: TeachersListProps) {
	if (courseSection.teacherCourseSectionEnrollments.length === 0) {
		return null;
	}

	const activeTeachers = filterMap(
		courseSection.teacherCourseSectionEnrollments,
		(e) => TeacherCourseSectionEnrollmentUtilities.isActive(e),
		(e) => e.teacher,
	);

	return <EmployeeComponents.EmployeesCondensedListButton label="Teacher" employees={activeTeachers} />;
}

type StudentsListProps = {
	courseSection: HeaderProps["courseSection"];
};

function StudentsList({ courseSection }: StudentsListProps) {
	if (courseSection.studentCourseSectionEnrollments.length === 0) {
		return null;
	}

	const activeStudents = filterMap(
		courseSection.studentCourseSectionEnrollments,
		(e) => StudentCourseSectionEnrollmentUtilities.isActive(e),
		(e) => e.student,
	);

	return <StudentComponents.StudentsCondensedListButton students={activeStudents} />;
}
